import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import LoadingSpinner from './common/LoadingSpinner';
import ConfirmationModal from './ConfirmationModal';
import { useFeedbackModal } from './common/useFeedbackModal';
import './UserPreferences.css';

function UserPreferences({ userId, onSave }) {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { modal, openModal, closeModal } = useFeedbackModal();
  const [formData, setFormData] = useState({
    allergies: [],
    foodPreferences: [],
    favoriteFoods: '',
    dislikedFoods: '',
    dailyCalorieGoal: null,
    prefersVegetarian: false,
    prefersLowCarb: false,
    prefersHighProtein: false,
    prefersHighFiber: false
  });
  const [newAllergy, setNewAllergy] = useState('');
  const [newPreference, setNewPreference] = useState('');

  const commonAllergies = ['Peanuts', 'Tree Nuts', 'Dairy', 'Eggs', 'Gluten', 'Soy', 'Fish', 'Shellfish', 'Sesame'];
  const commonPreferences = ['Spicy', 'Mild', 'Sweet', 'Savory', 'Crispy', 'Soft', 'Hot', 'Cold'];

  const loadPreferences = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const data = await api.getUserPreferences(userId);
      setPreferences(data);
      if (data) {
        setFormData({
          allergies: data.allergies || [],
          foodPreferences: data.foodPreferences || [],
          favoriteFoods: data.favoriteFoods || '',
          dislikedFoods: data.dislikedFoods || '',
          dailyCalorieGoal: data.dailyCalorieGoal || null,
          prefersVegetarian: data.prefersVegetarian || false,
          prefersLowCarb: data.prefersLowCarb || false,
          prefersHighProtein: data.prefersHighProtein || false,
          prefersHighFiber: data.prefersHighFiber || false
        });
      }
    } catch (error) {
      // 404 is expected when no preferences exist yet - not an error
      if (error.response?.status === 404 || error.status === 404) {
        // Silently handle - apiClient already logs this appropriately
        setPreferences(null);
      } else {
        console.error('Error loading preferences:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadPreferences();
    } else {
      // If no userId, stop loading immediately
      setLoading(false);
      setPreferences(null);
    }
  }, [userId, loadPreferences]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        userId,
        ...formData
      };
      const saved = await api.saveUserPreferences(data);
      setPreferences(saved);
      setShowForm(false);
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('preferences-updated', { 
        detail: { preferences: saved } 
      }));
      
      if (onSave) onSave();
    } catch (error) {
      console.error('Error saving preferences:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save preferences. Please try again.';
      openModal({
        title: 'Save failed',
        message: errorMessage,
        type: 'error'
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addAllergy = (allergy) => {
    if (allergy && !formData.allergies.includes(allergy)) {
      setFormData(prev => ({
        ...prev,
        allergies: [...prev.allergies, allergy]
      }));
      setNewAllergy('');
    }
  };

  const removeAllergy = (allergy) => {
    setFormData(prev => ({
      ...prev,
      allergies: prev.allergies.filter(a => a !== allergy)
    }));
  };

  const addPreference = (pref) => {
    if (pref && !formData.foodPreferences.includes(pref)) {
      setFormData(prev => ({
        ...prev,
        foodPreferences: [...prev.foodPreferences, pref]
      }));
      setNewPreference('');
    }
  };

  const removePreference = (pref) => {
    setFormData(prev => ({
      ...prev,
      foodPreferences: prev.foodPreferences.filter(p => p !== pref)
    }));
  };

  if (loading) {
    return (
      <div className="user-preferences">
        <LoadingSpinner message="Loading preferences..." />
      </div>
    );
  }

  return (
    <div className="user-preferences">
      <div className="preferences-header">
        <h3>⚙️ {t('preferences.title', 'Food Preferences & Allergies')}</h3>
        <button 
          className="btn-edit-small" 
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? '❌' : '✏️'} {showForm ? t('action.cancel', 'Cancel') : t('action.edit', 'Edit')}
        </button>
      </div>

      {!showForm && !preferences && (
        <div className="preferences-display">
          <p style={{ color: '#666', fontStyle: 'italic', padding: '10px 0' }}>
            {t('preferences.none.set', 'No preferences set yet. Click Edit to add your food preferences and allergies.')}
          </p>
        </div>
      )}

      {!showForm && preferences && (
        <div className="preferences-display">
          {preferences.allergies && preferences.allergies.length > 0 && (
            <div className="pref-item">
              <strong>🚫 {t('preferences.allergies', 'Allergies')}:</strong>
              <div className="tags">
                {preferences.allergies.map((allergy, idx) => (
                  <span key={idx} className="tag allergy-tag">{allergy}</span>
                ))}
              </div>
            </div>
          )}
          
          {preferences.foodPreferences && preferences.foodPreferences.length > 0 && (
            <div className="pref-item">
              <strong>❤️ {t('preferences.food.preferences', 'Food Preferences')}:</strong>
              <div className="tags">
                {preferences.foodPreferences.map((pref, idx) => (
                  <span key={idx} className="tag preference-tag">{pref}</span>
                ))}
              </div>
            </div>
          )}
          
          {preferences.favoriteFoods && (
            <div className="pref-item">
              <strong>⭐ {t('preferences.favorite.foods', 'Favorite Foods')}:</strong>
              <p>{preferences.favoriteFoods}</p>
            </div>
          )}
          
          {preferences.dislikedFoods && (
            <div className="pref-item">
              <strong>👎 {t('preferences.disliked.foods', 'Disliked Foods')}:</strong>
              <p>{preferences.dislikedFoods}</p>
            </div>
          )}
          
          {preferences.dailyCalorieGoal && (
            <div className="pref-item">
              <strong>🔥 {t('preferences.calorie.goal', 'Daily Calorie Goal')}:</strong>
              <p>{preferences.dailyCalorieGoal} calories</p>
            </div>
          )}
          
          <div className="pref-item">
            <strong>🥗 {t('preferences.diet.preferences', 'Diet Preferences')}:</strong>
            <div className="tags">
              {preferences.prefersVegetarian && <span className="tag">Vegetarian</span>}
              {preferences.prefersLowCarb && <span className="tag">Low Carb</span>}
              {preferences.prefersHighProtein && <span className="tag">High Protein</span>}
              {preferences.prefersHighFiber && <span className="tag">High Fiber</span>}
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="preferences-form">
          <div className="form-group">
            <label>{t('preferences.allergies', 'Allergies')}:</label>
            <div className="tag-input-group">
              <input
                type="text"
                value={newAllergy}
                onChange={(e) => setNewAllergy(e.target.value)}
                placeholder={t('preferences.add.allergy', 'Add allergy')}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addAllergy(newAllergy);
                  }
                }}
              />
              <button type="button" onClick={() => addAllergy(newAllergy)}>➕</button>
            </div>
            <div className="quick-tags">
              {commonAllergies.map(allergy => (
                <button
                  key={allergy}
                  type="button"
                  className="quick-tag"
                  onClick={() => addAllergy(allergy)}
                  disabled={formData.allergies.includes(allergy)}
                >
                  {allergy}
                </button>
              ))}
            </div>
            <div className="selected-tags">
              {formData.allergies.map((allergy, idx) => (
                <span key={idx} className="tag allergy-tag">
                  {allergy}
                  <button type="button" onClick={() => removeAllergy(allergy)}>×</button>
                </span>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>{t('preferences.food.preferences', 'Food Preferences')}:</label>
            <div className="tag-input-group">
              <input
                type="text"
                value={newPreference}
                onChange={(e) => setNewPreference(e.target.value)}
                placeholder={t('preferences.add.preference', 'Add preference')}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addPreference(newPreference);
                  }
                }}
              />
              <button type="button" onClick={() => addPreference(newPreference)}>➕</button>
            </div>
            <div className="quick-tags">
              {commonPreferences.map(pref => (
                <button
                  key={pref}
                  type="button"
                  className="quick-tag"
                  onClick={() => addPreference(pref)}
                  disabled={formData.foodPreferences.includes(pref)}
                >
                  {pref}
                </button>
              ))}
            </div>
            <div className="selected-tags">
              {formData.foodPreferences.map((pref, idx) => (
                <span key={idx} className="tag preference-tag">
                  {pref}
                  <button type="button" onClick={() => removePreference(pref)}>×</button>
                </span>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>{t('preferences.favorite.foods', 'Favorite Foods')} (comma-separated):</label>
            <input
              type="text"
              name="favoriteFoods"
              value={formData.favoriteFoods}
              onChange={handleChange}
              placeholder="e.g., Injera, Doro Wat, Lentils"
            />
          </div>

          <div className="form-group">
            <label>{t('preferences.disliked.foods', 'Disliked Foods')} (comma-separated):</label>
            <input
              type="text"
              name="dislikedFoods"
              value={formData.dislikedFoods}
              onChange={handleChange}
              placeholder="e.g., Raw fish, Spicy foods"
            />
          </div>

          <div className="form-group">
            <label>{t('preferences.calorie.goal', 'Daily Calorie Goal')} (optional):</label>
            <input
              type="number"
              name="dailyCalorieGoal"
              value={formData.dailyCalorieGoal || ''}
              onChange={handleChange}
              placeholder="e.g., 2200"
            />
          </div>

          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                name="prefersVegetarian"
                checked={formData.prefersVegetarian}
                onChange={handleChange}
              />
              🥗 {t('preferences.vegetarian', 'Prefer Vegetarian Options')}
            </label>
            <label>
              <input
                type="checkbox"
                name="prefersLowCarb"
                checked={formData.prefersLowCarb}
                onChange={handleChange}
              />
              🍞 {t('preferences.low.carb', 'Prefer Low Carb')}
            </label>
            <label>
              <input
                type="checkbox"
                name="prefersHighProtein"
                checked={formData.prefersHighProtein}
                onChange={handleChange}
              />
              🥩 {t('preferences.high.protein', 'Prefer High Protein')}
            </label>
            <label>
              <input
                type="checkbox"
                name="prefersHighFiber"
                checked={formData.prefersHighFiber}
                onChange={handleChange}
              />
              🌾 {t('preferences.high.fiber', 'Prefer High Fiber')}
            </label>
          </div>

          <button type="submit" className="btn-primary">
            💾 {t('action.save', 'Save Preferences')}
          </button>
        </form>
      )}

      <ConfirmationModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={() => {}}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText="OK"
        cancelText=""
      />
    </div>
  );
}

export default UserPreferences;

